/**
 * Inertia.js Vite Plugin
 *
 * This plugin provides two main features:
 *
 * 1. **Pages shorthand** - Transforms `pages: './Pages'` into a full `resolve` function
 *    that uses `import.meta.glob` to load page components. This saves users from writing
 *    boilerplate glob code in every project.
 *
 * 2. **SSR dev server** - During development, wraps the SSR entry file with server bootstrap
 *    code and exposes an HTTP endpoint that Laravel can call to render pages server-side.
 *    This eliminates the need to run a separate Node.js SSR server during development.
 *
 * The plugin is framework-agnostic - it detects which Inertia adapter (Vue, React, Svelte)
 * is being used by looking at import statements, then applies the appropriate transforms.
 * Custom frameworks can be added via the `frameworks` option.
 */

import { existsSync } from 'node:fs'
import { dirname, resolve } from 'node:path'
import { glob } from 'tinyglobby'
import type { Plugin, ViteDevServer } from 'vite'
import { defaultFrameworks } from './frameworks/index'
import { transformPageResolution } from './pagesTransform'
import { handleSSRRequest, InertiaSSROptions, resolveSSREntry, SSR_ENDPOINT, SSR_ENTRY_CANDIDATES } from './ssr'
import { findInertiaAppExport, wrapWithServerBootstrap } from './ssrTransform'
import type { FrameworkConfig } from './types'

export type { FrameworkConfig, SSRTemplate } from './types'
export { InertiaSSROptions }

export interface InertiaPluginOptions {
  /**
   * SSR configuration. Pass a string for the entry path, an object for
   * additional options, or `false` to disable SSR entirely. Auto-detected
   * when not specified from:
   * - resources/js/ssr.{ts,tsx,js,jsx}
   * - src/ssr.{ts,tsx,js,jsx}
   * - resources/js/app.{ts,tsx,js,jsx}
   * - src/app.{ts,tsx,js,jsx}
   */
  ssr?: string | false | InertiaSSROptions

  /**
   * Custom framework configurations. Use this to add support for frameworks
   * beyond the built-in Vue, React, and Svelte adapters.
   *
   * @example
   * ```ts
   * inertia({
   *   frameworks: {
   *     package: '@inertiajs/solid',
   *     extensions: ['.tsx', '.jsx'],
   *     extractDefault: true,
   *     ssr: (configureCall, options) => `
   *       import createServer from '@inertiajs/solid/server'
   *       const render = await ${configureCall}
   *       createServer((page) => render(page)${options})
   *     `,
   *   }
   * })
   * ```
   */
  frameworks?: FrameworkConfig | FrameworkConfig[]
}

/**
 * Normalize the frameworks option into a record keyed by package name.
 */
function toFrameworkRecord(input?: FrameworkConfig | FrameworkConfig[]): Record<string, FrameworkConfig> {
  if (!input) {
    return {}
  }

  const configs = Array.isArray(input) ? input : [input]

  return Object.fromEntries(configs.map((config) => [config.package, config]))
}

/**
 * Warm up page component files so Vite discovers their dependencies upfront.
 *
 * The pages shorthand (or default) is transformed into import.meta.glob during the transform
 * hook, which is too late for Vite's initial dependency scanner. Without warmup, first
 * navigation to a lazy-loaded page can trigger a disruptive re-optimization.
 */
async function warmupPageFiles(server: ViteDevServer, sourceFile: string, pageGlobs: string[]): Promise<void> {
  const files = await glob(pageGlobs, { cwd: dirname(sourceFile), absolute: true })

  files.forEach((file) => server.warmupRequest(file))
}

export default function inertia(options: InertiaPluginOptions = {}): Plugin {
  const ssrDisabled = options.ssr === false
  const ssr = typeof options.ssr === 'string' ? { entry: options.ssr } : options.ssr || {}
  const frameworks = { ...defaultFrameworks, ...toFrameworkRecord(options.frameworks) }

  let entry: string | null = null
  let devServer: ViteDevServer | null = null

  return {
    name: '@inertiajs/vite',

    config(config, env) {
      if (ssrDisabled || !env.isSsrBuild) {
        return
      }

      const root = config.root ?? process.cwd()
      const ssrEntry = ssr.entry ?? SSR_ENTRY_CANDIDATES.find((candidate) => existsSync(resolve(root, candidate)))

      return {
        build: {
          sourcemap: ssr.sourcemap !== false ? (config.build?.sourcemap ?? true) : undefined,
          rollupOptions: ssrEntry
            ? {
                input: config.build?.rollupOptions?.input ?? (config.build as any)?.rolldownOptions?.input ?? ssrEntry,
              }
            : undefined,
        },
      }
    },

    configResolved(config) {
      if (ssrDisabled) {
        return
      }

      entry = resolveSSREntry(ssr, config)

      if (entry && config.build?.ssr) {
        config.logger.info(`Inertia SSR entry: ${entry}`)
      }
    },

    transform(code, id, options) {
      if (!/\.[jt]sx?$/.test(id)) {
        return null
      }

      let result = code

      if (!ssrDisabled && options?.ssr && findInertiaAppExport(result)) {
        result =
          wrapWithServerBootstrap(
            result,
            { port: ssr.port, cluster: ssr.cluster, formatErrors: ssr.formatErrors },
            frameworks,
          ) ?? result
      }

      const pageTransform = transformPageResolution(result, frameworks)

      if (pageTransform) {
        if (devServer && pageTransform.pageGlobs.length > 0) {
          warmupPageFiles(devServer, id, pageTransform.pageGlobs).catch(() => {})
        }

        return pageTransform.code
      }

      return result !== code ? result : null
    },

    configureServer(server) {
      devServer = server

      if (!entry) {
        return
      }

      let warmingUp = false

      server.middlewares.use(SSR_ENDPOINT, async (req, res, next) => {
        if (req.method !== 'POST') {
          return next()
        }

        if (warmingUp) {
          server.config.logger.info('SSR skipped, module graph is still warming up...')
          res.setHeader('Content-Type', 'application/json')
          res.end(JSON.stringify(null))
          return
        }

        await handleSSRRequest(server, entry!, req, res, ssr.formatErrors ?? true)
      })

      server.config.logger.info(`Inertia SSR dev endpoint: ${SSR_ENDPOINT}`)

      server.httpServer?.once('listening', () => {
        warmingUp = true
        server.config.logger.info('Warming up Inertia SSR module graph...')

        server
          .ssrLoadModule(entry!)
          .then(() => server.config.logger.info('Inertia SSR module graph warmed up'))
          .catch((error) =>
            server.config.logger.warn(`Failed to warm up Inertia SSR module graph: ${error?.message ?? error}`),
          )
          .finally(() => {
            warmingUp = false
          })
      })
    },
  }
}
